import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EventManager } from '../events/EventManager';
import { KeyboardEvent as EngineKeyboardEvent } from '../events/KeyboardEvent';
import { Event as EngineEvent } from '../events/Event';

interface MobileControlsProps {
   visible?: boolean;
   placement?: 'overlay' | 'below';
}

const isTouchCapable = (): boolean => {
   if (typeof window === 'undefined') return false;
   // Prefer navigator.maxTouchPoints when available
   const hasTouchPoints = (navigator as any)?.maxTouchPoints > 0;
   const hasOntouch = 'ontouchstart' in window || 'ontouchstart' in document.documentElement;
   let coarse = false;
   try {
      coarse = !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
   } catch { }
   return hasTouchPoints || hasOntouch || coarse;
};

const publishKey = (type: 'keydown' | 'keyup', key: string): void => {
   const em = EventManager.getInstance();
   const evt = new EngineKeyboardEvent(type, key, key, false, false, false);
   em.publish(evt);
};

export const MobileControls: React.FC<MobileControlsProps> = ({ visible, placement = 'overlay' }) => {
   const containerRef = useRef<HTMLDivElement>(null);
   const shouldShow = useMemo(() => (visible ?? isTouchCapable()), [visible]);
   const [pressedKeys, setPressedKeys] = useState<Set<string>>(new Set());

   useEffect(() => {
      if (!shouldShow) return;
      const el = containerRef.current;
      if (!el) return;
      const prevent = (e: Event) => {
         e.preventDefault();
      };
      // Prevent scrolling while interacting with controls
      el.addEventListener('touchmove', prevent, { passive: false });
      el.addEventListener('wheel', prevent, { passive: false } as any);
      return () => {
         el.removeEventListener('touchmove', prevent as any);
         el.removeEventListener('wheel', prevent as any);
      };
   }, [shouldShow]);

   if (!shouldShow) return null;

   const press = (key: string) => (e?: any) => {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      // TEMP: debug feedback to verify taps are firing
      try { console.log('[MobileControls] down', key); } catch { }
      // Debounce taps to avoid rapid re-trigger
      (press as any)._t = (press as any)._t || { last: 0 };
      const now = Date.now();
      if (now - (press as any)._t.last < 180) return;
      (press as any)._t.last = now;

      if (key === 'p') {
         try { (window as any).pacmanControls?.pause?.(); } catch { }
         try { document.dispatchEvent(new CustomEvent('mobile:pause', { bubbles: true })); } catch { }
         try { EventManager.getInstance().publish(new EngineEvent('mobile_toggle_pause')); } catch { }
         // Also route via engine keydown so game can listen like arrows
         publishKey('keydown', key);
      } else if (key === 'm') {
         try { (window as any).pacmanControls?.mute?.(); } catch { }
         try { document.dispatchEvent(new CustomEvent('mobile:mute', { bubbles: true })); } catch { }
         try { EventManager.getInstance().publish(new EngineEvent('mobile_toggle_mute')); } catch { }
         publishKey('keydown', key);
      } else {
         try {
            const domEvt = new window.KeyboardEvent('keydown', { key, bubbles: true, cancelable: true });
            document.dispatchEvent(domEvt);
         } catch { }
         publishKey('keydown', key);
      }
      // Fire explicit DOM custom events for mobile browsers (iOS Safari, etc.) and engine events
      try {
         if (key === 'p') {
            document.dispatchEvent(new CustomEvent('mobile:pause', { bubbles: true }));
            EventManager.getInstance().publish(new EngineEvent('mobile_toggle_pause'));
            try { (window as any).pacmanControls?.pause?.(); } catch { }
         }
         if (key === 'm') {
            document.dispatchEvent(new CustomEvent('mobile:mute', { bubbles: true }));
            EventManager.getInstance().publish(new EngineEvent('mobile_toggle_mute'));
            try { (window as any).pacmanControls?.mute?.(); } catch { }
         }
      } catch { }
      setPressedKeys(prev => {
         const next = new Set(prev);
         next.add(key);
         return next;
      });
   };
   const release = (key: string) => (e?: any) => {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      // TEMP: debug feedback to verify taps are firing
      try { console.log('[MobileControls] up', key); } catch { }
      try {
         const domEvt = new window.KeyboardEvent('keyup', { key, bubbles: true, cancelable: true });
         document.dispatchEvent(domEvt);
      } catch { }
      publishKey('keyup', key);
      setPressedKeys(prev => {
         const next = new Set(prev);
         next.delete(key);
         return next;
      });
   };

   const attachHandlers = (key: string) => ({
      onPointerDown: press(key),
      onPointerUp: release(key),
      onPointerCancel: release(key),
      onPointerLeave: release(key),
   });

   const commonBtn: React.CSSProperties = {
      width: 56,
      height: 56,
      borderRadius: 12,
      background: 'rgba(0,0,0,0.45)',
      border: '2px solid rgba(0,229,255,0.6)',
      boxShadow: '0 0 8px rgba(0,229,255,0.45)',
      color: '#CCF7FF',
      fontWeight: 800,
      userSelect: 'none',
      touchAction: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      WebkitTapHighlightColor: 'transparent',
      cursor: 'pointer',
      transition: 'transform 120ms ease, background 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
   };

   const getBtnStyle = (key: string): React.CSSProperties => {
      const isPressed = pressedKeys.has(key);
      if (!isPressed) return commonBtn;
      return {
         ...commonBtn,
         transform: 'scale(0.96)',
         background: 'rgba(0,229,255,0.18)',
         border: '2px solid #00E5FF',
         boxShadow: '0 0 12px rgba(0,229,255,0.75)',
         color: '#E9FDFF',
      };
   };

   const isOverlay = placement === 'overlay';

   return (
      <div
         ref={containerRef}
         className="mobile-controls-root"
         style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 2147483647,
            height: '35%',

         }}
      >
         {/* Overlay layout (edge arrows + center controls) */}
         {isOverlay && (
            <>
               {/* Up - top left */}
               <div
                  style={{
                     position: 'absolute',
                     top: 12,
                     left: 50,
                     transform: 'none',
                     pointerEvents: 'auto',
                     touchAction: 'none',
                  }}
               >
                  <div style={getBtnStyle('w')} {...attachHandlers('w')}>▲</div>
               </div>

               {/* Up - top right */}
               <div
                  style={{
                     position: 'absolute',
                     top: 12,
                     right: 50,
                     transform: 'none',
                     pointerEvents: 'auto',
                     touchAction: 'none',
                  }}
               >
                  <div style={getBtnStyle('w')} {...attachHandlers('w')}>▲</div>
               </div>

               {/* Down - bottom left */}
               <div
                  style={{
                     position: 'absolute',
                     bottom: 12,
                     left: 50,
                     transform: 'none',
                     pointerEvents: 'auto',
                     touchAction: 'none',
                  }}
               >
                  <div style={getBtnStyle('s')} {...attachHandlers('s')}>▼</div>
               </div>

               {/* Down - bottom right */}
               <div
                  style={{
                     position: 'absolute',
                     bottom: 12,
                     right: 50,
                     transform: 'none',
                     pointerEvents: 'auto',
                     touchAction: 'none',
                  }}
               >
                  <div style={getBtnStyle('s')} {...attachHandlers('s')}>▼</div>
               </div>

               {/* Left - middle left */}
               <div
                  style={{
                     position: 'absolute',
                     top: '50%',
                     left: 12,
                     transform: 'translateY(-50%)',
                     pointerEvents: 'auto',
                     touchAction: 'none',
                  }}
               >
                  <div style={getBtnStyle('a')} {...attachHandlers('a')}>◀</div>
               </div>

               {/* Right - middle right */}
               <div
                  style={{
                     position: 'absolute',
                     top: '50%',
                     right: 12,
                     transform: 'translateY(-50%)',
                     pointerEvents: 'auto',
                     touchAction: 'none',
                  }}
               >
                  <div style={getBtnStyle('d')} {...attachHandlers('d')}>▶</div>
               </div>

               {/* Center controls: Pause and Mute */}
               <div
                  style={{
                     position: 'absolute',
                     top: '50%',
                     left: '50%',
                     transform: 'translate(-50%, -50%)',
                     display: 'flex',
                     gap: 12,
                     pointerEvents: 'auto',
                     touchAction: 'none',
                  }}
               >
                  <div
                     style={{
                        ...getBtnStyle('p'),
                        width: 60,
                        height: 60,
                        borderRadius: 30,
                     }}
                     title="Pause"
                     {...attachHandlers('p')}
                  >
                     II
                  </div>
                  <div
                     style={{
                        ...getBtnStyle('m'),
                        width: 60,
                        height: 60,
                        borderRadius: 30,
                     }}
                     title="Mute"
                     {...attachHandlers('m')}
                  >
                     🔇
                  </div>
               </div>
            </>
         )}

         {/* Below layout (compact cluster) */}
         {!isOverlay && (
            <div style={{
               display: 'flex',
               justifyContent: 'center',
               alignItems: 'center',
               gap: 16,
               width: '100%',
            }}>
               {/* D-Pad cluster */}
               <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 56px)',
                  gridTemplateRows: 'repeat(3, 56px)',
                  gap: 8,
               }}>
                  <div />
                  <div style={commonBtn} {...attachHandlers('w')}>▲</div>
                  <div />
                  <div style={commonBtn} {...attachHandlers('a')}>◀</div>
                  <div style={{ ...commonBtn, opacity: 0.6 }}>
                     •
                  </div>
                  <div style={commonBtn} {...attachHandlers('d')}>▶</div>
                  <div />
                  <div style={commonBtn} {...attachHandlers('s')}>▼</div>
                  <div />
               </div>

               {/* Pause/Mute to the right */}
               <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{
                     ...commonBtn,
                     width: 60,
                     height: 60,
                     borderRadius: 30,
                  }} {...attachHandlers('p')} title="Pause">II</div>
                  <div style={{
                     ...commonBtn,
                     width: 60,
                     height: 60,
                     borderRadius: 30,
                  }} {...attachHandlers('m')} title="Mute">🔇</div>
               </div>
            </div>
         )}
      </div>
   );
};

export default MobileControls;


